import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const REDBG = "#FDECEC";
const RED = "#DC2626";
const GREEN = "#16A34A";

const CommunityReports = () => {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState("");

  const load = () =>
    apiInstanceFetch
      .get(`community/admin/reports?status=1&start=0&limit=100`)
      .then((r) => {
        setReports(r.reports || []);
        setTotal(r.total || 0);
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const drop = (id) => {
    setReports((prev) => prev.filter((x) => x._id !== id));
    setTotal((t) => Math.max(0, t - 1));
  };

  const dismiss = (rep) => {
    setBusy(rep._id);
    apiInstanceFetch
      .patch(`community/admin/resolveReport`, { reportId: rep._id })
      .then(() => drop(rep._id))
      .catch(() => {})
      .finally(() => setBusy(""));
  };

  const removePost = (rep) => {
    const post = rep.postId;
    if (!post || !post._id) return;
    if (!window.confirm("Remove this post from the community? All its reports will be resolved.")) return;
    setBusy(rep._id);
    apiInstanceFetch
      .delete(`community/admin/post?postId=${post._id}`)
      .then(() => drop(rep._id))
      .catch(() => {})
      .finally(() => setBusy(""));
  };

  const personName = (u) => {
    if (!u) return "Unknown";
    return ((u.firstName || "") + " " + (u.lastName || "")).trim() || u.email || "Unknown";
  };

  return (
    <div className="mainCategory">
      <Title name="Community Reports" />

      <div className="d-flex align-items-center" style={{ gap: 10, margin: "16px 0" }}>
        <i className="fa-solid fa-flag" style={{ color: RED }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>Pending reports ({total})</span>
        <button onClick={load} className="btn btn-sm" style={{ marginLeft: "auto", border: `1px solid ${HAIRLINE}`, color: INK, fontWeight: 600 }}>
          <i className="fa-solid fa-rotate-right" style={{ marginRight: 6 }} />
          Refresh
        </button>
      </div>

      {reports.length === 0 ? (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 48, textAlign: "center", color: MUTED }}>
          <i className="fa-solid fa-circle-check" style={{ fontSize: 34, color: GREEN }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No pending reports</div>
          <div style={{ fontSize: 13 }}>Reported community posts will appear here for review.</div>
        </div>
      ) : (
        reports.map((rep) => {
          const post = rep.postId || {};
          const author = post.userId;
          const media = Array.isArray(post.media) ? post.media : [];
          return (
            <div key={rep._id} style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 14, marginBottom: 12 }}>
              <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: RED, background: REDBG, borderRadius: 20, padding: "2px 10px" }}>
                  {rep.reason || "Reported"}
                </span>
                <span style={{ fontSize: 12, color: MUTED }}>
                  in <b style={{ color: INK }}>{rep.communityId ? rep.communityId.name : "—"}</b>
                </span>
                <span style={{ marginLeft: "auto", fontSize: 11.5, color: MUTED }}>{dayjs(rep.createdAt).format("DD MMM YYYY HH:mm")}</span>
              </div>

              <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 8 }}>
                Reported by <b style={{ color: INK }}>{personName(rep.reportedBy)}</b>
                {rep.note ? ` · “${rep.note}”` : ""}
              </div>

              {/* the reported post */}
              <div style={{ background: CREAM, border: `1px solid ${HAIRLINE}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, marginBottom: 4 }}>
                  {personName(author)}
                  {post.isDeleted ? (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: RED, background: REDBG, borderRadius: 20, padding: "1px 8px" }}>
                      Hidden
                    </span>
                  ) : null}
                </div>
                {post.text ? <div style={{ fontSize: 13.5, color: INK, whiteSpace: "pre-wrap" }}>{post.text}</div> : null}
                {media.length > 0 && (
                  <div className="d-flex" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    {media.map((md, i) =>
                      md.type === "video" ? (
                        <a key={i} href={md.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: GOLD, fontWeight: 600 }}>
                          <i className="fa-solid fa-play" style={{ marginRight: 5 }} />
                          View video
                        </a>
                      ) : (
                        <img key={i} src={md.url} alt="" style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 8, border: `1px solid ${HAIRLINE}` }} />
                      )
                    )}
                  </div>
                )}
              </div>

              <div className="d-flex" style={{ gap: 8, marginTop: 12 }}>
                <button
                  disabled={busy === rep._id}
                  onClick={() => removePost(rep)}
                  style={{ border: "none", background: REDBG, color: RED, fontWeight: 700, padding: "8px 14px", borderRadius: 9, cursor: "pointer" }}
                >
                  <i className="fa-solid fa-trash" style={{ marginRight: 6 }} />
                  Remove post
                </button>
                <button
                  disabled={busy === rep._id}
                  onClick={() => dismiss(rep)}
                  style={{ border: `1px solid ${HAIRLINE}`, background: "#fff", color: INK, fontWeight: 600, padding: "8px 14px", borderRadius: 9, cursor: "pointer" }}
                >
                  <i className="fa-solid fa-check" style={{ marginRight: 6 }} />
                  Dismiss
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default CommunityReports;
